import { AgentAttachedTrack } from './agentAttachedTrack';

export type TrackMention = {
  start: number;
  end: number;
  query: string;
};

export const COMPLETED_TRACK_TOKEN = /@\[[^\]]+\]\s*\(ch:\/\/track\/[^)]+\)/g;

export const formatTrackMentionToken = (track: AgentAttachedTrack): string =>
  `@[${track.artist} — ${track.title}](ch://track/${track.id})`;

/** Ionic sometimes inserts a space before the track URI — collapse it back. */
export const normalizeTrackTokenText = (text: string): string =>
  text.replace(
    /@\[([^\]]+)\]\s+\(ch:\/\/track\/([^)]+)\)/g,
    '@[$1](ch://track/$2)',
  );

export const insertTrackMentionToken = (
  text: string,
  mention: TrackMention,
  track: AgentAttachedTrack,
): string => {
  const token = formatTrackMentionToken(track);
  const before = text.slice(0, mention.start);
  const after = text.slice(mention.end);
  const spacer = after.startsWith(' ') || after === '' ? '' : ' ';
  return normalizeTrackTokenText(`${before}${token}${spacer}${after}`);
};

export const insertTrackTokenAt = (
  text: string,
  caret: number,
  track: AgentAttachedTrack,
): string => {
  const token = formatTrackMentionToken(track);
  const index = Math.max(0, Math.min(caret, text.length));
  const before = text.slice(0, index);
  const after = text.slice(index);
  const prefix = before.length > 0 && !/\s$/.test(before) ? ' ' : '';
  const suffix = after.length > 0 && !/^\s/.test(after) ? ' ' : '';
  return normalizeTrackTokenText(`${before}${prefix}${token}${suffix}${after}`);
};

const completedTrackTokenRanges = (
  text: string,
): Array<{ start: number; end: number }> => {
  const ranges: Array<{ start: number; end: number }> = [];
  for (const match of text.matchAll(COMPLETED_TRACK_TOKEN)) {
    if (match.index === undefined) continue;
    ranges.push({
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return ranges;
};

const isInsideRange = (
  index: number,
  ranges: Array<{ start: number; end: number }>,
): boolean => ranges.some((range) => index >= range.start && index < range.end);

const isTrackTokenAt = (text: string, atIndex: number): boolean => {
  if (text[atIndex] !== '@' || text[atIndex + 1] !== '[') return false;
  const close = text.indexOf(']', atIndex + 2);
  if (close === -1) return false;
  const tail = text.slice(close + 1);
  return /^\s*\(ch:\/\/track\/[^)]*\)?/.test(tail);
};

/** Active @mention at the caret — must start after whitespace or at position 0. */
export const parseTrackMention = (
  text: string,
  caret: number,
): TrackMention | null => {
  const normalized = normalizeTrackTokenText(text);
  const protectedRanges = completedTrackTokenRanges(normalized);
  const index = Math.max(0, Math.min(caret, normalized.length));
  const before = normalized.slice(0, index);
  let at = before.lastIndexOf('@');

  while (at !== -1) {
    if (isInsideRange(at, protectedRanges) || isTrackTokenAt(before, at)) {
      at = before.lastIndexOf('@', at - 1);
      continue;
    }
    if (at > 0 && !/\s/.test(before[at - 1] ?? '')) {
      at = before.lastIndexOf('@', at - 1);
      continue;
    }

    const query = before.slice(at + 1);
    if (!query.includes('\n')) {
      return { start: at, end: index, query };
    }

    at = before.lastIndexOf('@', at - 1);
  }

  return null;
};

export const removeTrackMention = (
  text: string,
  mention: TrackMention,
): string =>
  `${text.slice(0, mention.start)}${text.slice(mention.end)}`.replace(
    /\s{2,}/g,
    ' ',
  );

/** Drop resolved track tokens from composer text once they become a card. */
export const stripCompletedTrackTokens = (text: string): string =>
  normalizeTrackTokenText(text)
    .replace(COMPLETED_TRACK_TOKEN, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s+/, '');
